import { useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import L from 'leaflet';
import { TROMSO_CENTER, mockTrails } from './mockData';

// Define interfaces
interface Trail {
  id: string;
  name: string;
  description: string;
  difficulty: 'easy' | 'moderate' | 'difficult';
  distance: number; // in kilometers
  coordinates: [number, number][]; // array of [lat, lng]
  points: PointOfInterest[];
  color: string;
}

interface PointOfInterest {
  id: string;
  name: string;
  type: 'viewpoint' | 'rest' | 'wildlife' | 'facility';
  coordinates: [number, number];
  description: string;
}

export function TrailsMap() {
  const [selectedTrail, setSelectedTrail] = useState<Trail | null>(null);
  const [editMode, setEditMode] = useState(false);
  const center: [number, number] = TROMSO_CENTER; // Default center

  // Custom marker icons
  const getMarkerIcon = (type: string) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `<div class="w-8 h-8 rounded-full bg-white border-2 border-primary flex items-center justify-center">
        ${type === 'viewpoint' ? '👁️' : type === 'rest' ? '⌛' : type === 'wildlife' ? '🦁' : '🏢'}
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  };

  return (
    <div className="grid grid-cols-[300px_1fr] gap-6">
      {/* Sidebar */}
      <div className="space-y-4">
        {/* Trail List */}
        <Card>
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Trails</h3>
              <Button size="sm" variant="outline" onClick={() => setEditMode(!editMode)}>
                {editMode ? 'View Mode' : 'Edit Mode'}
              </Button>
            </div>
            <div className="space-y-2">
              {mockTrails.map(trail => (
                <div
                  key={trail.id}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedTrail?.id === trail.id
                      ? 'bg-primary text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setSelectedTrail(trail as Trail)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{trail.name}</span>
                    <Badge variant={
                      trail.difficulty === 'easy' ? 'default' :
                      trail.difficulty === 'moderate' ? 'secondary' : 'destructive'
                    }>
                      {trail.difficulty}
                    </Badge>
                  </div>
                  <div className="text-sm mt-1 opacity-80">
                    {trail.distance}km • {trail.points.length} points
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Trail Details */}
        {selectedTrail && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-2">{selectedTrail.name}</h3>
              <p className="text-sm text-gray-600 mb-4">{selectedTrail.description}</p>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Points of Interest</label>
                  {selectedTrail.points.map(point => (
                    <div key={point.id} className="mt-2 p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{point.name}</span>
                        <Badge>{point.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{point.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Map */}
      <Card>
        <CardContent className="p-0">
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: '700px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            {mockTrails.map(trail => (
              <div key={trail.id}>
                <Polyline
                  positions={trail.coordinates.map(([lat, lng]) => [lng, lat])}
                  color={trail.color}
                  weight={4}
                  opacity={0.8}
                />
                
                {trail.points.map(point => (
                  <Marker
                    key={point.id}
                    position={[point.coordinates[1], point.coordinates[0]]}
                    icon={getMarkerIcon(point.type)}
                  >
                    <Popup>
                      <div className="p-2">
                        <h4 className="font-semibold">{point.name}</h4>
                        <p className="text-sm">{point.description}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </div>
            ))}
          </MapContainer>
        </CardContent>
      </Card>
    </div>
  );
}